import { Component, OnInit, SimpleChanges } from '@angular/core';
import { FieldType } from '@ngx-formly/core';
import { FormControl } from '@angular/forms';
import countries from 'world-countries';
import { Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'formly-field-phone',
  styleUrl: './phone-type.component.scss',
  templateUrl: `./phone-type.component.html`,
  standalone: false,
})
export class PhoneTypeComponent extends FieldType implements OnInit {
  private destroy$ = new Subject<void>();

  numberControl = new FormControl('');

  selectedDialCode = '+39';
  private lastDialCodeProp?: string;

  /**
   * True quando il prefisso è stato stabilito esplicitamente: scelto a mano dall'utente
   * dalla tendina, copiato da un altro campo telefono, oppure dedotto da un numero già
   * valorizzato all'apertura del form. Da quel momento il link con il campo nazione
   * (`linkCountryField`) non lo sovrascrive più.
   */
  private userTouchedDialCode = false;

  countries: {
    name: string;
    dialCode: string;
    isoCode: string;
    flag: string;
  }[] = countries.map((c) => ({
    name: c.name.common,
    dialCode: c.idd.root + (c.idd.suffixes?.[0] || ''),
    isoCode: c.cca2,
    flag: `https://flagcdn.com/${c.cca2.toLowerCase()}.svg`,
  }));

  ngOnInit() {
    this.numberControl = new FormControl('');

    const defaultCode = this.props?.['defaultDialCode'];
    if (defaultCode) {
      this.selectedDialCode = defaultCode;
    }
    this.parseInitialValue();

    if (this.formControl.disabled) {
      this.numberControl.disable({ emitEvent: false });
    }

    this.formControl.statusChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (this.formControl.disabled && !this.numberControl.disabled) {
          this.numberControl.disable({ emitEvent: false });
        } else if (!this.formControl.disabled && this.numberControl.disabled) {
          this.numberControl.enable({ emitEvent: false });
        }
      });

    this.numberControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.updateValue());

    this.linkCountryFieldPrefix();

    // Reconcile the host formControl's value+validity with the parsed sub-controls
    // on load. Without this, updateValue() only ever runs on user interaction
    // (dial-code change / number input), so a field initialised from the model keeps
    // whatever raw/invalid state the stored value had — leaving the hosting form
    // invalid and its Save button disabled until the user happens to touch the prefix.
    // Deferred to avoid ExpressionChangedAfterItHasBeenChecked during the host's init.
    Promise.resolve().then(() => this.updateValue());
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get shouldShowLabel(): boolean {
    return this.props['showLabel'] !== false;
  }

  get shouldShowCopyLink(): boolean {
    return this.props['showCopyFromPhone'] === true;
  }

  get canShowCopyLink(): boolean {
    if (this.props['copyFromField'] && this.props['copyFromForm']) {
      const sourceForm = this.props['copyFromForm'];
      const sourceFieldKey = this.props['copyFromField'];
      return sourceForm && sourceForm.get && sourceForm.get(sourceFieldKey);
    }
    return false;
  }

  get isDisabled(): boolean {
    return this.formControl.disabled;
  }

  private parseInitialValue() {
    const currentValue = this.formControl.value;

    if (
      !currentValue ||
      currentValue === null ||
      (typeof currentValue === 'string' && currentValue.trim() === '')
    ) {
      this.numberControl.setValue('', { emitEvent: false });
      return;
    }

    if (typeof currentValue === 'string') {
      const value = currentValue.trim();
      const match = this.countries.find(
        (c: any) =>
          c.dialCode && c.dialCode.length > 0 && value.startsWith(c.dialCode)
      );

      if (match && match.dialCode) {
        this.selectedDialCode = match.dialCode;
        this.userTouchedDialCode = true;
        const numberPart = value.slice(match.dialCode.length).trim();
        this.numberControl.setValue(numberPart, { emitEvent: false });
      } else {
        const cleanedValue = value.replace(/^\+\d+\s*/, '');
        this.numberControl.setValue(cleanedValue, { emitEvent: false });
      }
    }
  }

  onDialCodeChange() {
    this.userTouchedDialCode = true;
    this.updateValue();
  }

  copyFromPhoneNumber() {
    const sourceForm = this.props['copyFromForm'];
    const sourceFieldKey = this.props['copyFromField'];

    if (sourceForm && sourceFieldKey) {
      const sourceControl = sourceForm.get(sourceFieldKey);

      if (sourceControl && sourceControl.value) {
        const sourceValue = sourceControl.value.trim();

        const match = this.countries.find(
          (c: any) =>
            c.dialCode &&
            c.dialCode.length > 0 &&
            sourceValue.startsWith(c.dialCode)
        );

        if (match) {
          this.selectedDialCode = match.dialCode;
          this.userTouchedDialCode = true;
          const numberPart = sourceValue.slice(match.dialCode.length).trim();
          this.numberControl.setValue(numberPart);
        } else {
          this.numberControl.setValue(sourceValue.replace(/^\+\d+\s*/, ''));
        }

        this.updateValue();
      }
    }
  }

  updateValue() {
    let phoneNumber = this.numberControl.value?.trim() || '';

    const cleaned = phoneNumber.replace(/\D/g, '');
    if (cleaned !== phoneNumber && phoneNumber) {
      this.numberControl.setValue(cleaned, { emitEvent: false });
      phoneNumber = cleaned;
    }

    if (!phoneNumber || phoneNumber === '') {
      this.formControl.setValue(null);
      if (this.props.required) {
        this.formControl.setErrors({ required: true });
      } else {
        this.formControl.setErrors(null);
      }
      return;
    }

    if (phoneNumber.length < 6 || phoneNumber.length > 14) {
      const full = `${this.selectedDialCode} ${phoneNumber}`.trim();
      this.formControl.setValue(full);
      this.formControl.setErrors({ phone: true });
      return;
    }

    const full = `${this.selectedDialCode} ${phoneNumber}`.trim();
    this.formControl.setValue(full);
    this.formControl.setErrors(null);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['props'] && this.props?.['defaultDialCode']) {
      const newCode = this.props['defaultDialCode'];
      if (newCode !== this.selectedDialCode) {
        this.setDialCode(newCode);
      }
    }
  }

  ngDoCheck() {
    const currentDialCodeProp = this.props?.['defaultDialCode'];
    if (currentDialCodeProp && currentDialCodeProp !== this.lastDialCodeProp) {
      this.lastDialCodeProp = currentDialCodeProp;
      this.setDialCode(currentDialCodeProp);
    }
  }

  private isValidDialCode(code: string): boolean {
    return this.countries.some((c) => c.dialCode === code);
  }

  /**
   * Deriva il prefisso internazionale (dial code) dal codice ISO2 di un paese,
   * cercandolo nella lista `world-countries`. Restituisce undefined se il codice
   * non è mappato.
   */
  private getDialCodeFromIso(isoCode?: string): string | undefined {
    if (!isoCode) return undefined;
    const match = this.countries.find(
      (c) => c.isoCode === isoCode.toUpperCase()
    );
    return match?.dialCode || undefined;
  }

  /**
   * Se il campo espone la prop `linkCountryField` (chiave di un campo country
   * fratello nello stesso form), aggiorna automaticamente il prefisso in base
   * alla nazione selezionata — anche se il numero è già stato digitato, così chi
   * compila prima il telefono e poi la nazione non resta con il prefisso di default.
   * L'unico caso in cui non si interviene è quando il prefisso è già stato stabilito
   * esplicitamente (vedi `userTouchedDialCode`).
   */
  private linkCountryFieldPrefix() {
    const linkCountryField = this.props?.['linkCountryField'];
    if (!linkCountryField) return;

    const countryControl = this.field?.form?.get(linkCountryField);
    if (!countryControl) return;

    countryControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((location: any) => {
        if (this.userTouchedDialCode) return;
        const dialCode = this.getDialCodeFromIso(location?.countryCode);
        if (dialCode) {
          this.setDialCode(dialCode);
        }
      });
  }

  setDialCode(code?: string) {
    if (code && code !== this.selectedDialCode && this.isValidDialCode(code)) {
      this.selectedDialCode = code;

      const currentValue = this.formControl.value;
      if (typeof currentValue === 'string' && currentValue.trim()) {
        const oldDialCodeMatch = this.countries.find(
          (c) =>
            c.dialCode &&
            c.dialCode.length > 0 &&
            currentValue.startsWith(c.dialCode)
        );

        if (oldDialCodeMatch) {
          const numberPart = currentValue
            .slice(oldDialCodeMatch.dialCode.length)
            .trim();
          this.numberControl.setValue(numberPart, { emitEvent: false });
        } else {
          const numberPart = currentValue.replace(/^\+\d+\s*/, '').trim();
          this.numberControl.setValue(numberPart, { emitEvent: false });
        }
      }

      this.updateValue();
    }
  }
}